import { PlaybookCardSection } from "../PlaybookCardSection";
import { Avatar, Col, ConfigProvider, Form } from "antd";
import { useTranslate } from "#src/hooks";
import { FormItemCol } from "../FormItemCol";
import { ObjectivesSelect } from "../ObjectivesSelect";
import { useMemo } from "react";
import { mapToOptions, mapToThemeOptions } from "../../helpers";
import { ThemeResponse } from "pages/media/components/themeEditor/hooks/useThemeApi";
import { SelectableAspectRatio, ASPECT_RATIO_OPTIONS } from "../../types";
import { DurationFormItem } from "../DurationFormItem";
import { BrandKit, ContactResponse } from "pages/media/types/";
import { AspectRatioSegmented } from "components/AspectRatioSegmented/";
import { useAntTheme } from "hooks/useAntTheme/";
import { UserOutlined } from "@ant-design/icons";
import { BrandKitSelect } from "../BrandKitSelect";

export const BasicSection = ({
  publicThemes,
  orgThemes,
  brandKits,
  contacts,
  selectedAspectRatio,
  durationEnabled,
  localDuration,
  handleDurationToggle,
  handleDurationChange,
}: {
  publicThemes: ThemeResponse[];
  orgThemes: ThemeResponse[];
  brandKits: BrandKit[];
  contacts: ContactResponse[];
  selectedAspectRatio?: SelectableAspectRatio;
  durationEnabled: boolean;
  localDuration: number;
  handleDurationToggle: (value: boolean) => void;
  handleDurationChange: (value: number) => void;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const themeOptions = useMemo(
    () => mapToThemeOptions(publicThemes, orgThemes, t, selectedAspectRatio),
    [publicThemes, orgThemes, t, selectedAspectRatio]
  );
  const brandKitOptions = mapToOptions(brandKits);
  const contactOptions = contacts.map((contact) => {
    const presenterUrl =
      contact.presenter.face_profile?.thumbnail_asset_path ??
      contact.presenter.face_profile?.extracted_asset_path ??
      contact.presenter.face_profile?.custom_asset_path;
    return {
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            src={presenterUrl}
            size={20}
            shape="square"
            icon={<UserOutlined />}
          />
          {contact.firstname} {contact.lastname}
        </span>
      ),
      value: contact.id,
    };
  });

  return (
    <>
      <Col xs={24}>
        <PlaybookCardSection
          title={t("pages.playbooks.sections.basic.basicSettings")}
        />
      </Col>
      <FormItemCol
        name="brief"
        label={t("pages.playbooks.sections.basic.prompt")}
        component="textarea"
        placeholder={t("pages.playbooks.sections.basic.promptPlaceholder")}
        rows={5}
      />
      <Col xs={24}>
        <Form.Item
          label={t("pages.playbooks.sections.basic.objectives")}
          name="objectives"
        >
          <ObjectivesSelect />
        </Form.Item>
      </Col>
      <FormItemCol
        name="language"
        label={t("pages.playbooks.defaultLanguage")}
        component="language"
        placeholder={t("pages.playbooks.sections.basic.selectLanguage")}
        colSpan={{ xs: 24, md: 12 }}
      />
      <FormItemCol
        name="theme_id"
        label={t("pages.playbooks.defaultTheme")}
        component="select"
        optionGroups={themeOptions}
        placeholder={t("pages.playbooks.sections.basic.selectTheme")}
        colSpan={{ xs: 24, md: 12 }}
      />
      <Col xs={24} md={{ span: 12 }}>
        <DurationFormItem
          enabled={durationEnabled}
          formValue={localDuration}
          onToggleChange={handleDurationToggle}
          onDurationChange={handleDurationChange}
        />
      </Col>
      <Col xs={24} md={{ span: 12 }}>
        <Form.Item
          label={t("pages.playbooks.defaultBrandKit")}
          name="brand_kit_id"
        >
          <BrandKitSelect
            options={brandKitOptions}
            placeholder={t("pages.playbooks.selectBrandKit")}
          />
        </Form.Item>
        <FormItemCol
          name="generate_brandkit"
          component="toggle"
          toggleLabel={t("pages.playbooks.sections.basic.customizeBrandkit")}
          helpText={t("pages.playbooks.sections.basic.customizeBrandkitHelp")}
        />
      </Col>
      <Col xs={24} md={{ span: 12 }}>
        <ConfigProvider
          theme={{
            components: {
              Segmented: {
                itemSelectedBg: theme.colorPrimary,
                itemSelectedColor: theme.colorBgContainer,
              },
            },
          }}
        >
          <Form.Item
            label={t("pages.playbooks.aspectRatio")}
            name="aspect_ratio"
          >
            <AspectRatioSegmented
              block
              options={ASPECT_RATIO_OPTIONS.map((value) => ({
                type: value,
                value,
              }))}
            />
          </Form.Item>
        </ConfigProvider>
      </Col>
      <FormItemCol
        name="contact_id"
        label={t("pages.playbooks.defaultContact")}
        component="toggleSelect"
        toggleLabel={t("pages.playbooks.sections.basic.useDefaultContact")}
        options={contactOptions}
        placeholder={t("pages.playbooks.selectContact")}
        helpText={t("pages.playbooks.sections.basic.useDefaultContactInfo")}
        colSpan={{ xs: 24, md: 12 }}
      />
    </>
  );
};
